import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import {
  useGetVehicleHistoryQuery,
  useGetVehicleExpensesQuery,
} from "../../../store/appApi";

const AssignedDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isFetching, isError } = useGetVehicleHistoryQuery(
    id,
    { skip: !id },
  );

  const loading = isLoading || isFetching;
  const vehicle = data?.vehicle ?? {};
  const current = data?.currentAssignment ?? null;
  const history = Array.isArray(data?.history) ? data.history : [];

  const { data: expenseData, isLoading: fuelLoading } =
    useGetVehicleExpensesQuery(id, { skip: !id });
  const fuelLogs = Array.isArray(expenseData?.expenses)
    ? expenseData.expenses
    : [];
  const fuelTotalCost = fuelLogs.reduce(
    (sum, r) => sum + (Number(r.amount) || 0),
    0,
  );

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";

  const fmtKm = (n) =>
    n != null && n !== "" ? `${Number(n).toLocaleString("en-IN")} km` : "—";

  return (
    <>
      <PageTitle
        activeMenu="Assignment History"
        motherMenu="Vehicles / Machines"
      />

      {/* ── Vehicle Meta + Current Assignment ──────────────────────────────── */}
      <div className="row g-3 mb-3">
        {/* Vehicle Info Card */}
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="fa fa-car me-2 text-primary" />
                Vehicle Details
              </h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-3">
                  <span className="spinner-border spinner-border-sm me-2" />
                  Loading…
                </div>
              ) : isError ? (
                <div className="alert alert-danger mb-0">
                  Failed to load vehicle details.
                </div>
              ) : (
                <dl className="row mb-0">
                  <dt className="col-5 text-muted small">Vehicle Number</dt>
                  <dd
                    className="col-7 fw-bold fs-5 mb-2"
                    style={{ textTransform: "uppercase" }}
                  >
                    {vehicle.vehicleNumber || "—"}
                  </dd>

                  <dt className="col-5 text-muted small">Make</dt>
                  <dd
                    className="col-7 mb-2"
                    style={{ textTransform: "capitalize" }}
                  >
                    {vehicle.vehicleMake || "—"}
                  </dd>

                  <dt className="col-5 text-muted small">Model</dt>
                  <dd className="col-7 mb-2">{vehicle.vehicleModel || "—"}</dd>

                  <dt className="col-5 text-muted small">Company</dt>
                  <dd className="col-7 mb-2">{vehicle.companyName || "—"}</dd>

                  <dt className="col-5 text-muted small">Status</dt>
                  <dd className="col-7 mb-0">
                    <span
                      className={`badge ${vehicle.status ? "badge-success" : "badge-danger"}`}
                    >
                      {vehicle.status ? "Active" : "Inactive"}
                    </span>
                  </dd>
                </dl>
              )}
            </div>
          </div>
        </div>

        {/* Current Assignment Card */}
        <div className="col-md-6">
          <div
            className="card h-100"
            style={{
              border: current ? "1px solid #4caf50" : "1px solid #dee2e6",
            }}
          >
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i
                  className={`fa ${current ? "fa-user-check text-success" : "fa-user-times text-muted"} me-2`}
                />
                Current Assignment
              </h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-3">
                  <span className="spinner-border spinner-border-sm me-2" />
                  Loading…
                </div>
              ) : current ? (
                <>
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="rounded-circle bg-success d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                      style={{
                        width: 48,
                        height: 48,
                        fontSize: 20,
                        color: "#fff",
                      }}
                    >
                      {current.employeeName?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="fw-bold fs-6">{current.employeeName}</div>
                      <div className="text-muted small">
                        {current?.employeePhone}
                      </div>
                      <div className="text-muted small">
                        {history[0]?.employeeRole}
                      </div>
                    </div>
                    <span className="badge badge-success ms-auto">Active</span>
                  </div>
                  <dl className="row mb-0">
                    <dt className="col-5 text-muted small">Assigned Since</dt>
                    <dd className="col-7 mb-2">{fmtDate(current.startDate)}</dd>
                    <dt className="col-5 text-muted small">Starting Km</dt>
                    <dd className="col-7 mb-0">{fmtKm(current.startKm)}</dd>
                  </dl>
                </>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fa fa-car fa-2x mb-2 d-block" />
                  This vehicle is not currently assigned to anyone.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Fuel / Consumption Log ──────────────────────────────────────── */}
      <div className="row g-3 mb-3">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <h5 className="card-title mb-0">
                <i className="fa fa-tint me-2 text-warning" />
                Fuel / Consumption Log
              </h5>
              {!fuelLoading && fuelLogs.length > 0 && (
                <>
                  <span className="badge badge-warning">
                    {fuelLogs.length} entries
                  </span>
                  <span className="ms-auto text-muted medium text-primary">
                    Total Cost:{" "}
                    <strong>
                      ₹
                      {Number(fuelTotalCost).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </span>
                </>
              )}
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="display dataTable no-footer w-100">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Amount (₹)</th>
                      <th>Description</th>
                      <th>Posted By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fuelLoading && (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" />
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!fuelLoading && fuelLogs.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-4 text-muted">
                          No expense entries logged for this vehicle yet.
                        </td>
                      </tr>
                    )}
                    {!fuelLoading &&
                      fuelLogs.map((log) => (
                        <tr key={log._id}>
                          <td className="text-muted small">
                            {fmtDate(log.created)}
                          </td>
                          <td className="fw-semibold small">
                            {log.categoryName || "—"}
                          </td>
                          <td className="small fw-semibold text-success">
                            ₹
                            {Number(log.amount).toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="text-muted small">
                            {log.desc || "—"}
                          </td>
                          <td className="small">{log.postedBy || "—"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Assignment History Table ─────────────────────────────────────── */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <h5 className="card-title mb-0">
                <i className="fa fa-history me-2 text-primary" />
                Assignment History
              </h5>
              {!loading && (
                <span className="badge badge-primary">{history.length}</span>
              )}
              <button
                className="btn btn-sm btn-outline-secondary ms-auto"
                onClick={() => navigate(-1)}
              >
                ← Back
              </button>
            </div>

            <div className="card-body">
              {isError && (
                <div className="alert alert-danger">
                  Failed to load assignment history. Please try again.
                </div>
              )}

              <div className="table-responsive">
                <table className="display dataTable no-footer w-100">
                  <thead>
                    <tr>
                      {/* <th>#</th> */}
                      <th>Employee</th>
                      <th>Phone</th>
                      <th>Role</th>
                      <th>From Date</th>
                      <th>To Date</th>
                      <th>Start Km</th>
                      <th>End Km</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={9} className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" />
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!loading && history.length === 0 && !isError && (
                      <tr>
                        <td colSpan={9} className="text-center py-4 text-muted">
                          No assignment history found for this vehicle.
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      history.map((h, idx) => (
                        <tr
                          key={h._id}
                          style={
                            h.isActive
                              ? { background: "rgba(76, 175, 80, 0.06)" }
                              : {}
                          }
                        >
                          {/* <td className="text-muted small">{idx + 1}</td> */}
                          <td className="fw-semibold">{h.employeeName}</td>
                          <td className="text-muted small">
                            {h.employeePhone || "—"}
                          </td>
                          <td>
                            <span
                              className="badge badge-light"
                              style={{ textTransform: "capitalize" }}
                            >
                              {h.employeeRole?.replace(/-/g, " ") || "—"}
                            </span>
                          </td>
                          <td className="text-muted small">
                            {fmtDate(h.startDate)}
                          </td>
                          <td className="text-muted small">
                            {h.endDate ? (
                              fmtDate(h.endDate)
                            ) : (
                              <span className="text-success fw-semibold">
                                Present
                              </span>
                            )}
                          </td>
                          <td className="text-muted small">
                            {fmtKm(h.startKm)}
                          </td>
                          <td className="text-muted small">{fmtKm(h.endKm)}</td>
                          <td>
                            {h.isActive ? (
                              <span className="badge badge-success">
                                Active
                              </span>
                            ) : (
                              <span className="badge badge-secondary">
                                Closed
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignedDetail;
